type ElementHandlerAttributesArgument = {
    [attributeName: string]: string
};

type ElementHandlerChildrenArgument = HTMLElement | (HTMLElement | string)[];
type ElementHandlerEventsArgument = {
    [E in keyof HTMLElementEventMap]?: (event: HTMLElementEventMap[E]) => void;
};

type UseElementFunction = <E extends HTMLElement>(
    htmlElement: E,
    classNameOrAttributes?: string | ElementHandlerAttributesArgument | null,
    innerTextOrChildrenElements?: string | ElementHandlerChildrenArgument | null,
    events?: ElementHandlerEventsArgument
) => E;

type CreateElementFunction = <T extends keyof HTMLElementTagNameMap>(
    tagName: T,
    classNameOrAttributes?: string | ElementHandlerAttributesArgument | null,
    innerTextOrChildrenElements?: string | ElementHandlerChildrenArgument | null,
    events?: ElementHandlerEventsArgument
) => HTMLElementTagNameMap[T];

type AwaitElementFunction = (
    querySelector: string,
    attempts?: number,
    timeout?: number
) => Promise<HTMLElement>;

type FindElementFunction = <T extends HTMLElement>(
    querySelector: string,
    parentElement?: HTMLElement | Element | Document
) => T | null;

type FindElementAllFunction = <T extends HTMLElement>(
    querySelector: string,
    parentElement?: HTMLElement | Element | Document
) => T[];

type ElementHasContentFunction = (
    element: HTMLElement
) => Boolean;

type ElementIsInViewportFunction = (
    element: HTMLElement
) => Boolean;

export const useElement: UseElementFunction = (
    htmlElement, classNameOrAttributes, innerTextOrChildrenElements, events
) => {
    if (classNameOrAttributes) {
        if (typeof classNameOrAttributes === 'string') {
            htmlElement.classList.add(classNameOrAttributes);
        }
        else {
            Object.keys(classNameOrAttributes).forEach(function (propName) {
                if (propName === 'className') {
                    htmlElement.setAttribute('class', classNameOrAttributes[propName]);
                }
                else if (propName === 'addClass') {
                    var existingClassNames = htmlElement.getAttribute('class');
                    htmlElement.setAttribute('class', (existingClassNames ? (existingClassNames + ' ') : '' ) + classNameOrAttributes[propName]);
                }
                else {
                    htmlElement.setAttribute(propName, classNameOrAttributes[propName]);
                }
            });
        }
    }

    if (innerTextOrChildrenElements) {
        if (typeof innerTextOrChildrenElements === 'string') {
            htmlElement.innerText = innerTextOrChildrenElements;
        }
        else if (Array.isArray(innerTextOrChildrenElements)) {
            innerTextOrChildrenElements.forEach(function (childElement) {
                if (typeof childElement === 'string') {
                    htmlElement.appendChild(document.createTextNode(childElement));
                }
                else {
                    htmlElement.appendChild(childElement);
                }
            });
        }
        else {
            htmlElement.appendChild(innerTextOrChildrenElements);
        }
    }

    if (events) {
        const eventNames = Object.keys(events) as [keyof typeof events];
        eventNames.forEach(function (eventName) {
            htmlElement.addEventListener(eventName, events[eventName] as () => void);
        });
    }

    return htmlElement;
};

export const createElement: CreateElementFunction = (
    tagName, classNameOrAttributes, innerTextOrChildrenElements, events
) => useElement(
    document.createElement(tagName), classNameOrAttributes, innerTextOrChildrenElements, events
);

export const selectElement = <T extends HTMLElement>(
    querySelector: string,
    successCallback?: ((selectedElement: T) => void) | null,
    failureCallback?: (() => void) | null
): T | null => {
    const selectedElement = document.querySelector(querySelector);

    (successCallback && selectedElement) && successCallback(selectedElement as T);
    (failureCallback && !selectedElement) && failureCallback();

    return selectedElement
        ? selectedElement as T
        : null;
};

export const selectedElementAll = <T extends HTMLElement>(
    querySelector: string,
    eachSuccessCallback?: ((selectedElement: T) => void) | null,
    failureCallback?: (() => void) | null
): T[] | null => {
    const resultElementList = Array.from(document.querySelectorAll(querySelector)) as T[];

    if (resultElementList.length) {
        eachSuccessCallback && resultElementList.forEach(eachSuccessCallback);
        return resultElementList;
    }
    
    failureCallback && failureCallback();
    return null;
};

export const awaitElement: AwaitElementFunction = (
    querySelector, attempts = 1, timeout = 500
) => new Promise((resolve, reject) => {
    if (attempts < 1) {
        throw 'awaitElement: "attempts" argument cannot be less than 1. (Default value = 1)';
    }

    if (timeout < 10) {
        throw 'awaitElement: "timeout" argument cannot be less than 10. (Default value = 500)';
    }

    let remainingAttempts = attempts;
    checkForElement();

    function checkForElement() {
        remainingAttempts--;
        const checkedElement = document.querySelector(querySelector);
    
        if (checkedElement) {
            resolve(checkedElement as HTMLElement);
        }
        else if (remainingAttempts > 0) {
            setTimeout(checkForElement, timeout);
        }
        else {
            reject();
        }
    }
});

export const findElement: FindElementFunction = (
    querySelector, parentElement = document
) => parentElement.querySelector(querySelector);

export const findElementAll: FindElementAllFunction = (
    querySelector, parentElement = document
) => Array.from(parentElement.querySelectorAll(querySelector));

export const elementHasContent: ElementHasContentFunction = (
    element
) => element.innerHTML.trim().length > 0;

export const elementIsInViewport: ElementIsInViewportFunction = (
    element
) => {
    let currentElement = element;
    let top = currentElement.offsetTop;
    let left = currentElement.offsetLeft;
    let width = currentElement.offsetWidth;
    let height = currentElement.offsetHeight;
  
    while(currentElement.offsetParent) {
        currentElement = currentElement.offsetParent as HTMLElement;
        top += currentElement.offsetTop;
        left += currentElement.offsetLeft;
    }
  
    return (
        top < (window.scrollY + window.innerHeight) &&
        left < (window.scrollX + window.innerWidth) &&
        (top + height) > window.scrollY &&
        (left + width) > window.scrollX
    );
};

export const onChangeElementVisibility = (
    element: HTMLElement,
    callback: (isVisible: boolean) => void
) => {
    var options = {
        root: document.documentElement
    };
  
    var observer = new IntersectionObserver(function (entries, observer) {
        entries.forEach(function (entry) {
            callback(entry.intersectionRatio > 0);
        });
    }, options);
  
    observer.observe(element);
};
